import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  View,
  Text,
  Pressable,
  Platform,
  Linking,
  BackHandler,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import metadata from '../build-metadata.json';

// Optional: Expo Updates (guarded so this file still works without the module)
let Updates: typeof import('expo-updates') | undefined;
try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  Updates = require('expo-updates');
} catch {
  Updates = undefined;
}

interface MandatoryVersionCheckProps {
  children: React.ReactNode;
}

interface ServerMetadata {
  buildMajor: number;
  buildMinor: number;
  buildRevision: number;
  buildBranch: string;
}

const APP_STORE_HTTP = 'https://apps.apple.com/us/app/meanderev/id6742797523';
const APP_STORE_DEEPLINK = 'itms-apps://itunes.apple.com/app/id6742797523'; // opens App Store directly
const PLAY_STORE_HTTP = 'https://play.google.com/store/apps/details?id=com.meanderev.charge';
const PLAY_STORE_DEEPLINK = 'market://details?id=com.meanderev.charge';

// A small helper so we don't forget to fall back
const openStoreUrl = async (primary: string, fallback: string) => {
  try {
    const can = await Linking.canOpenURL(primary);
    if (can) {
      await Linking.openURL(primary);
      return;
    }
  } catch {}
  await Linking.openURL(fallback);
};

const MandatoryVersionCheck: React.FC<MandatoryVersionCheckProps> = ({ children }) => {
  const [updateRequired, setUpdateRequired] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [serverVersion, setServerVersion] = useState('');
  const [otaState, setOtaState] = useState<
    | { phase: 'idle' }
    | { phase: 'checking' }
    | { phase: 'downloading' }
    | { phase: 'reloading' }
    | { phase: 'failed'; error: string }
  >({ phase: 'idle' });

  // prevent duplicate fetch/updates
  const didCheckRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const getVersionCheckUrl = (): string => {
    // Your original logic retained, but simplified
    if (metadata.buildBranch === 'dev' || metadata.buildBranch === 'stage') {
      return 'https://stage.meanderev.co/build-metadata.json';
    }
    return 'https://app.meanderev.co/build-metadata.json';
  };

  const compareRequiresUpdate = (serverData: ServerMetadata): boolean => {
    const currentMajor = metadata.buildMajor;
    const currentMinor = metadata.buildMinor;
    const serverMajor = serverData.buildMajor;
    const serverMinor = serverData.buildMinor;

    // Require update if server MAJOR or MINOR is higher
    return serverMajor > currentMajor || (serverMajor === currentMajor && serverMinor > currentMinor);
  };

  const checkVersion = async () => {
    if (didCheckRef.current) return;
    didCheckRef.current = true;

    // Skip check on local dev web if you want
    if (Platform.OS === 'web' && process.env.REACT_NATIVE_ENV === 'development') {
      setIsChecking(false);
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000); // 8s timeout to avoid hanging at splash
    try {
      const versionCheckUrl = getVersionCheckUrl();
      const response = await fetch(versionCheckUrl, {
        cache: 'no-cache',
        headers: { 'Cache-Control': 'no-cache' },
        signal: controller.signal,
      });
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }
      const serverData: ServerMetadata = await response.json();
      if (!mountedRef.current) return;

      setServerVersion(`${serverData.buildMajor}.${serverData.buildMinor}.${serverData.buildRevision}`);

      const needsUpdate = compareRequiresUpdate(serverData);

      // Log for diagnostics
      console.log('Version check:', {
        current: `${metadata.buildMajor}.${metadata.buildMinor}.${metadata.buildRevision}`,
        server: `${serverData.buildMajor}.${serverData.buildMinor}.${serverData.buildRevision}`,
        needsUpdate,
      });

      setUpdateRequired(needsUpdate);

      // If a mandatory update is required, try OTA first (if available).
      // If OTA succeeds and the new bundle boots with updated metadata, this modal will not show.
      if (needsUpdate && Updates && Platform.OS !== 'web') {
        // Fire & forget; we still present the modal, but we show "Downloading update..." state
        tryOtaUpdate();
      }
    } catch (err: any) {
      console.error('Error checking version:', err?.message || err);
      // Fail open: allow the app. If you prefer fail-closed, set updateRequired(true) here.
    } finally {
      clearTimeout(timeout);
      if (mountedRef.current) setIsChecking(false);
    }
  };

  const tryOtaUpdate = async () => {
    if (!Updates) return;
    try {
      setOtaState({ phase: 'checking' });
      const result = await Updates.checkForUpdateAsync();
      if (!mountedRef.current) return;

      if (result.isAvailable) {
        setOtaState({ phase: 'downloading' });
        await Updates.fetchUpdateAsync();
        if (!mountedRef.current) return;

        setOtaState({ phase: 'reloading' });
        // This reload will boot the new bundle; if that bundle has updated metadata, the modal will not appear.
        await Updates.reloadAsync();
      } else {
        setOtaState({ phase: 'idle' });
      }
    } catch (e: any) {
      if (!mountedRef.current) return;
      console.warn('OTA update failed:', e?.message || e);
      setOtaState({ phase: 'failed', error: String(e?.message || e) });
    }
  };

  useEffect(() => {
    checkVersion();
  }, []);

  // Block the Android back button if update is required
  useEffect(() => {
    if (Platform.OS === 'android' && updateRequired) {
      const sub = BackHandler.addEventListener('hardwareBackPress', () => true);
      return () => sub.remove();
    }
  }, [updateRequired]);

  const handleUpdate = async () => {
    if (Platform.OS === 'ios') {
      await openStoreUrl(APP_STORE_DEEPLINK, APP_STORE_HTTP);
    } else if (Platform.OS === 'android') {
      await openStoreUrl(PLAY_STORE_DEEPLINK, PLAY_STORE_HTTP);
    } else {
      // Web: point them to your site or the stores; leaving iOS App Store as default
      await Linking.openURL(APP_STORE_HTTP);
    }
  };

  const handleCancel = () => {
    // iOS: do nothing; modal is blocking. (We purposely do not render the button on iOS.)
    if (Platform.OS === 'web') {
      window.close();
      setTimeout(() => {
        alert('Please close this tab and update to the latest version.');
      }, 50);
      return;
    }
    if (Platform.OS === 'android') {
      BackHandler.exitApp();
    }
  };

  // Loading view during initial check
  if (isChecking) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#39BEB7" />
        <Text style={styles.loadingText}>Checking for updates...</Text>
      </View>
    );
  }

  // No update required → render children
  if (!updateRequired) {
    return <>{children}</>;
  }

  const showExitButton = Platform.OS !== 'ios'; // Hide on iOS to enforce blocking
  const otaLabel =
    otaState.phase === 'checking'
      ? 'Checking for an immediate update...'
      : otaState.phase === 'downloading'
      ? 'Downloading update...'
      : otaState.phase === 'reloading'
      ? 'Applying update...'
      : otaState.phase === 'failed'
      ? 'Could not download update automatically.'
      : '';

  return (
    <>
      {children}
      <Modal
        visible={true}
        transparent
        animationType="fade"
        // Important: keep it blocking. On Android, onRequestClose fires on back press; return no-op.
        onRequestClose={() => {}}
        statusBarTranslucent
        hardwareAccelerated
        presentationStyle="overFullScreen"
      >
        <View style={styles.modalOverlay} pointerEvents="auto">
          <View style={styles.modalContent}>
            <View style={styles.iconContainer}>
              <Text style={styles.iconText}>⚠️</Text>
            </View>

            <Text style={styles.modalTitle}>Update Required</Text>

            <Text style={styles.modalMessage}>
              A newer version of MeanderEV is available. Please update to continue using the app.
            </Text>

            {!!serverVersion && (
              <Text style={styles.versionInfo}>New version: {serverVersion}</Text>
            )}

            {otaState.phase !== 'idle' && (
              <View style={styles.otaRow}>
                {(otaState.phase === 'checking' ||
                  otaState.phase === 'downloading' ||
                  otaState.phase === 'reloading') && (
                  <ActivityIndicator size="small" />
                )}
                <Text style={styles.otaText}>
                  {otaLabel}
                  {otaState.phase === 'failed' ? ' You can still update from the store.' : ''}
                </Text>
              </View>
            )}

            <View style={styles.buttonContainer}>
              <Pressable
                style={[styles.button, styles.updateButton]}
                onPress={handleUpdate}
              >
                <Text style={styles.updateButtonText}>Update Now</Text>
              </Pressable>

              {showExitButton && (
                <Pressable
                  style={[styles.button, styles.cancelButton]}
                  onPress={handleCancel}
                >
                  <Text style={styles.cancelButtonText}>
                    {Platform.OS === 'web' ? 'Close Tab' : 'Exit App'}
                  </Text>
                </Pressable>
              )}
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666666',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 420,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  iconContainer: { marginBottom: 16 },
  iconText: { fontSize: 48 },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#23262B',
    marginBottom: 12,
    textAlign: 'center',
  },
  modalMessage: {
    fontSize: 16,
    color: '#666666',
    textAlign: 'center',
    marginBottom: 12,
    lineHeight: 24,
  },
  versionInfo: {
    fontSize: 14,
    color: '#39BEB7',
    marginBottom: 16,
    fontWeight: '600',
  },
  otaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
  },
  otaText: {
    fontSize: 14,
    color: '#666666',
  },
  buttonContainer: {
    width: '100%',
    gap: 12,
  },
  button: {
    width: '100%',
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
  },
  updateButton: {
    backgroundColor: '#39BEB7',
  },
  updateButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: 'bold',
  },
  cancelButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#CCCCCC',
  },
  cancelButtonText: {
    color: '#666666',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default MandatoryVersionCheck;
